import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, EASE } from "@/components/Reveal";

const FAQS = [
  {
    q: "What is P.E.E. OS?",
    a: "P.E.E. OS is a trade execution operating system built around the Position • Event • Entry framework. It organizes market context, qualifying events, entries, risk levels, trade status, and analytics into one disciplined execution process.",
  },
  {
    q: "What does P.E.E. stand for?",
    a: "Position • Event • Entry. Know your position. Wait for the event. Execute the entry.",
  },
  {
    q: "Is P.E.E. OS just another trading indicator?",
    a: "No. An indicator outputs a signal. P.E.E. OS is a structured execution workflow — position context, event recognition, validation, trade mapping, lifecycle alerts, and performance analytics working together as one system.",
  },
  {
    q: "Does P.E.E. OS automatically execute trades?",
    a: "No. P.E.E. OS structures and organizes execution information. Every trading decision and every order remains entirely yours.",
  },
  {
    q: "Does P.E.E. OS guarantee profitable trades?",
    a: "No. Trading futures and other financial instruments involves substantial risk. P.E.E. OS does not guarantee profitable trades, and past performance does not guarantee future results.",
  },
  {
    q: "What markets does P.E.E. support?",
    a: "P.E.E. OS is demonstrated on index futures such as NQ. The current list of supported markets and timeframes is maintained inside the member area.",
  },
  {
    q: "Do I need TradingView?",
    a: "P.E.E. OS is designed to work alongside charting platforms such as TradingView. Setup and access instructions are included with membership.",
  },
  {
    q: "What is Execution Traders Community?",
    a: "The ecosystem around P.E.E. OS — Executor Training, live coaching, trade review, accountability, and a private community focused on developing disciplined traders.",
  },
  {
    q: "What is included with membership?",
    a: "P.E.E. OS access, Execution Traders Community, Executor Training, P.E.E. Analytics, alerts, and community and training resources.",
  },
  {
    q: "What happens after joining?",
    a: "Your member account is created, onboarding begins with the Start Here training path, and you receive P.E.E. OS setup instructions and community access.",
  },
  {
    q: "Can beginners join?",
    a: "Yes. The P.E.E. Foundations path is designed to take you from the framework basics upward, with risk education first. No prior system experience is required.",
  },
  {
    q: "What is included in the annual membership?",
    a: "Everything included in the monthly membership, for 12 months — at a meaningfully lower effective monthly cost.",
  },
  {
    q: "Can I pay the annual membership over time?",
    a: "Flexible payment options may be available at checkout for eligible customers. Available methods are shown by Stripe at checkout.",
  },
  {
    q: "How does cancellation work?",
    a: "You can cancel according to the membership terms. Monthly membership simply stops at the end of the current billing period. Full details are in the Terms and Refund Policy.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="border-t border-line bg-[#070809] py-24 lg:py-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <SectionHeading
          chapter="12 — FAQ"
          align="center"
          testid="faq-heading"
          title={
            <>
              Questions. <span className="text-neon">Answered.</span>
            </>
          }
        />

        <div className="mt-14">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={0.03 * i}>
              <div
                data-testid={`faq-item-${i}`}
                className="border-b border-line"
              >
                <button
                  data-testid={`faq-trigger-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-6 py-5 text-left"
                >
                  <span
                    className={`font-display text-base font-bold tracking-tight transition-colors duration-300 sm:text-lg ${
                      open === i ? "text-neon" : "text-white"
                    }`}
                  >
                    {f.q}
                  </span>
                  <ChevronDown
                    size={17}
                    className={`shrink-0 transition-transform duration-500 ${
                      open === i ? "rotate-180 text-neon" : "text-dim"
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="pb-6 pr-10 text-sm leading-relaxed text-mist"
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
